"use client";

import { useState } from "react";
import Image from "next/image";
import {
  getStockCampPhoto,
  pickStockCampPhotoSubcategorySlug,
} from "@/lib/camps/stock-photos";

/**
 * #2112 — the alt text used to hard-code "summer camp" for every listing, so a
 * Friday swim-lesson series was announced to a screen reader as a summer camp.
 * Callers pass the configured offering noun (offeringNounForListingType); the
 * default keeps every existing camp caller's alt text byte-identical.
 */
const DEFAULT_OFFERING_NOUN = "summer camp";

export function CampPhoto({
  photoUrl,
  campName,
  offeringNoun = DEFAULT_OFFERING_NOUN,
  categorySlug,
  subcategorySlug,
  subcategorySlugs,
  subjectSlugs,
  formatSlugs,
  subcategoryImageUrl,
  categoryImageUrl,
  locationName,
  className = "",
  aspectRatio = "3/2",
}: {
  photoUrl: string | null | undefined;
  campName: string;
  /** Configured noun for this listing type ("class", "clinic", …). */
  offeringNoun?: string;
  categorySlug?: string | null;
  subcategorySlug?: string | null;
  subcategorySlugs?: string[] | null;
  subjectSlugs?: string[] | null;
  formatSlugs?: string[] | null;
  subcategoryImageUrl?: string | null;
  categoryImageUrl?: string | null;
  locationName?: string;
  className?: string;
  aspectRatio?: string;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const hasPhoto = photoUrl && photoUrl.trim() !== "" && !imgFailed;

  if (hasPhoto) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl ${className}`}
        style={{ aspectRatio }}
      >
        <Image
          src={photoUrl}
          alt={`Photo of ${campName}${categorySlug ? ` — ${categorySlug} ${offeringNoun}` : ` — ${offeringNoun}`}${locationName ? ` in ${locationName}` : ""}`}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className={`object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgFailed(true)}
        />
        {!imgLoaded && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-cloud via-white to-cloud" />
        )}
      </div>
    );
  }

  const selectedSubcategorySlug = pickStockCampPhotoSubcategorySlug({
    categorySlug,
    subjectSlugs,
    formatSlugs,
    subcategorySlugs,
    preferredSubcategorySlug: subcategorySlug,
  });
  const stockPhoto =
    subcategoryImageUrl?.trim() ||
    categoryImageUrl?.trim() ||
    getStockCampPhoto({ categorySlug, subcategorySlug: selectedSubcategorySlug });

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      style={{ aspectRatio }}
    >
      <Image
        src={stockPhoto}
        alt=""
        fill
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        className="object-cover"
      />
    </div>
  );
}
